import {
  faInfoCircle,
  faPlus,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useRef, useState } from "react";
import { useFormContext, useFieldArray, useFormState } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { PredefinedValueEditor } from "src/base-components/schemaKit/PredefinedValueEditor";
import {
  SortableField,
  SortableFieldsContext,
} from "src/base-components/schemaKit/SortableFields";
import { getPredefinedValueUniqueValidator } from "src/base-components/schemaKit/validators";
import { EnumColor } from "src/clients/features-control/api";
import { PredefinedValuesSchemaProperty } from "src/entities/queries";

export type PredefinedValuesForm = {
  properties: {
    [propertyName: string]: PredefinedValuesSchemaProperty;
  };
};

export const PredefinedValuesEditor = ({
  propertyName,
}: {
  propertyName: string;
}) => {
  const { formState } = useFormContext<PredefinedValuesForm>();
  const { fields, append, remove, move } = useFieldArray<PredefinedValuesForm>({
    name: `properties.${propertyName}._values`,
  });
  const [editableEnumValues, setEditableEnumValues] = useState([] as string[]);

  const fieldsRef = useRef(fields);

  fieldsRef.current = fields;

  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      setEditableEnumValues([]);
    }
  }, [formState.submitCount, formState.isSubmitSuccessful]);

  return (
    <div className="ml-2.5 flex flex-col gap-y-3 border-l border-gray-200 pl-4">
      <SortableFieldsContext
        items={fields.map((field) => field.id)}
        onChangeOrder={({ oldIndex, newIndex }) => {
          if (oldIndex !== newIndex) {
            move(oldIndex, newIndex);
          }
        }}
      >
        {fields.map((field, index) => (
          <SortableField key={field.id} id={field.id}>
            <EnumValueFields
              immutable={!editableEnumValues.includes(field.id)}
              index={index}
              propertyName={propertyName}
              onRemove={remove}
            />
          </SortableField>
        ))}
      </SortableFieldsContext>
      <div>
        <Button
          dataLoc={`add-${propertyName}-value`}
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() => {
            append(
              {
                _internal_name: "",
                _color: EnumColor.GRAY,
              },
              {
                focusName: `properties.${propertyName}._values.${fields.length}._internal_name`,
              },
            );

            setTimeout(() => {
              const lastField = fieldsRef.current.at(-1);
              if (lastField) {
                setEditableEnumValues([...editableEnumValues, lastField.id]);
              }
            });
          }}
        >
          Add value
        </Button>
      </div>
    </div>
  );
};

const EnumValueFields = ({
  propertyName,
  index,
  immutable,
  onRemove,
}: {
  propertyName: string;
  index: number;
  immutable: boolean;
  onRemove: (index: number) => void;
}) => {
  const formState = useFormState<PredefinedValuesForm>();

  return (
    <div
      className="flex items-start gap-x-2"
      data-loc={`${propertyName}-predefined-value-row`}
    >
      <div className="flex-1">
        <PredefinedValueEditor
          colorField={{
            name: `properties.${propertyName}._values.${index}._color`,
          }}
          dataLoc={`${propertyName}-predefined`}
          disabled={immutable ? ["value"] : false}
          error={
            !immutable &&
            formState.errors.properties?.[propertyName]?._values?.[index]
              ?._internal_name?.message
          }
          suffixIcon={
            immutable
              ? undefined
              : {
                  icon: faInfoCircle,
                  helpText: "Newly added values cannot be changed once saved.",
                }
          }
          valueField={{
            name: `properties.${propertyName}._values.${index}._internal_name`,
            rules: {
              required: "Value is required",
              validate: {
                unique: getPredefinedValueUniqueValidator(
                  `properties.${propertyName}._values`,
                  "_internal_name",
                ),
              },
            },
          }}
        />
      </div>
      {!immutable && (
        <div className="pt-1">
          <Icon
            color="text-gray-500 hover:text-red-600"
            dataLoc="predefined-value-delete"
            icon={faTrashAlt}
            size="xs"
            onClick={() => onRemove(index)}
          />
        </div>
      )}
    </div>
  );
};
